import {
    BoolFieldElement,
    DoubleFieldElement,
    InputMode,
    IntFieldElement,
    StringFieldElement,
} from '@kcftech/prometheustypescriptapiclient';
import { UseFormReturn } from 'react-hook-form';
import { ParameterType } from 'utils/assembly_application';

type BaseAssemblyParameterFormData = {
    name: string;
    code: string;
};

export type AssemblyParameterFormData = TypedAssemblyParameterFormData<ParameterType>;

type TypedParameterData<T, TType extends ParameterType, TElement = T> = {
    type: TType;
    defaultValue: T | undefined;
    validValues: TElement[] | undefined;
} & ExtraType<TType>;

type ExtraType<TType> = TType extends 'integer' | 'decimal'
    ? AssemblyNumberParameterData
    : Record<never, never>;

export type AssemblyNumberParameterData = {
    minValue: number | undefined;
    maxValue: number | undefined;
    minValueInclusive: boolean | undefined;
    maxValueInclusive: boolean | undefined;
};

export type AssemblyParameterFormInputProps = Readonly<{
    form: UseFormReturn<AssemblyParameterFormData>;
}>;
export type AssemblyParameterElementDto =
    | StringFieldElement
    | IntFieldElement
    | DoubleFieldElement
    | BoolFieldElement;

export type TypedAssemblyParameterFormData<
    TType extends ParameterType
> = BaseAssemblyParameterFormData &
    (TType extends 'bool'
        ? TypedParameterData<boolean, 'bool'>
        : TType extends 'integer'
        ? TypedParameterData<number, 'integer'>
        : TType extends 'decimal'
        ? TypedParameterData<number, 'decimal'>
        : TType extends 'text'
        ? TypedParameterData<string, 'text'>
        : TType extends 'selection'
        ? TypedParameterData<string, 'selection', string>
        : never);

export type AssemblyParameterNumberFormData = TypedAssemblyParameterFormData<
    'integer' | 'decimal'
>;

type ParamTypeToFormType<TType extends ParameterType> = UseFormReturn<
    TypedAssemblyParameterFormData<TType>
>;

export const typeGuardAssemblyParameterForm = <TType extends ParameterType>(
    form: UseFormReturn<AssemblyParameterFormData>,
    type: TType
): ParamTypeToFormType<TType> => {
    const formType = form.watch('type');
    if (formType !== type) {
        throw new Error('Form data does not match expected type');
    }

    return (form as unknown) as ParamTypeToFormType<TType>;
};

export const getParameterType = (
    parameter: AssemblyParameterElementDto
): ParameterType => {
    if (parameter instanceof DoubleFieldElement) {
        return 'decimal';
    }
    if (parameter instanceof IntFieldElement) {
        return 'integer';
    }
    if (parameter instanceof StringFieldElement) {
        if (parameter.InputMode === InputMode.Raw) {
            return 'text';
        }
        return 'selection';
    }
    if (parameter instanceof BoolFieldElement) {
        return 'bool';
    }

    throw new Error(`Unrecognized type for parameter: ${parameter}`);
};
